/**
 * The command palette's match and grouping (S6; docs/audits/PILOT-QUOTES-SHELL.md, S6 as built).
 *
 * A palette entry, as every provider returns it:
 *   { id, group, label, hint, available: true | reason, run(env) }
 * env = { returnTarget }: the element focused when the palette was opened.
 *
 * Unavailable entries are matched and shown with their reason, as the actions menu shows a
 * disabled action (S2); they are never hidden.
 */

/** The groups, in the order the palette shows them. A group not listed here goes last. */
export const PALETTE_GROUPS = ['Actions', 'Tools', 'Views', 'Layout', 'Recent', 'Navigate', 'Shell'];

const isWordChar = (c) => /[a-z0-9]/i.test(c);

/**
 * A subsequence match of `query` in `label`, case-insensitive. null when it does not match;
 * 0 for an empty query; otherwise higher is better: a letter at a word start or right after
 * the previous match scores more than a scattered one.
 */
export function fuzzyScore(label, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return 0;
  const text = String(label || '').toLowerCase();
  let score = 0;
  let at = -1;
  for (const ch of q) {
    const found = text.indexOf(ch, at + 1);
    if (found === -1) return null;
    score += 1;
    if (found === at + 1) score += 3;
    if (found === 0 || !isWordChar(text[found - 1])) score += 5;
    at = found;
  }
  return score;
}

/**
 * The entries matching `query`, grouped in PALETTE_GROUPS order. Within a group the better
 * match comes first; ties (and an empty query) keep the providers' order.
 * @returns {Array<{ group: string, entries: Array }>}
 */
export function groupEntries(entries, query) {
  const byGroup = new Map();
  (entries || []).forEach((entry, index) => {
    const score = fuzzyScore(entry.label, query);
    if (score === null) return;
    if (!byGroup.has(entry.group)) byGroup.set(entry.group, []);
    byGroup.get(entry.group).push({ entry, score, index });
  });
  const rank = (group) => {
    const i = PALETTE_GROUPS.indexOf(group);
    return i === -1 ? PALETTE_GROUPS.length : i;
  };
  return [...byGroup.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map((group) => ({
      group,
      entries: byGroup.get(group)
        .sort((a, b) => (b.score - a.score) || (a.index - b.index))
        .map((m) => m.entry)
    }));
}
